import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, EyeOff, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function InstitutionalLoginPage() {
  const { login, register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', full_name: '', institution_name: '' });

  if (isAuthenticated) {
    navigate('/dashboard/accreditation');
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Welcome back');
      navigate('/dashboard/accreditation');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerForm.email, registerForm.password, registerForm.full_name, 'accreditation');
      toast.success('Account created successfully');
      navigate('/dashboard/accreditation');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-[#F8FAFC] py-16" data-testid="institutional-login-page">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#1B2A4A] rounded-sm flex items-center justify-center mx-auto mb-3">
            <Building2 className="h-7 w-7 text-[#C5A572]" />
          </div>
          <h1 className="text-2xl font-bold text-[#1B2A4A] font-heading">Institutional Portal</h1>
          <p className="text-sm text-slate-500 mt-1">Division of Institutional Accreditation</p>
        </div>

        <Card className="imcac-card">
          <CardContent className="p-6">
            <Tabs defaultValue="login">
              <TabsList className="w-full mb-6 bg-[#F1F5F9]">
                <TabsTrigger value="login" className="flex-1 data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="inst-login-tab">
                  Sign In
                </TabsTrigger>
                <TabsTrigger value="register" className="flex-1 data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="inst-register-tab">
                  Register
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="inst-login-email" className="text-sm font-medium text-slate-700">Email Address</Label>
                    <Input
                      id="inst-login-email"
                      type="email"
                      placeholder="institution@example.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                      className="rounded-sm mt-1"
                      data-testid="inst-login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="inst-login-password" className="text-sm font-medium text-slate-700">Password</Label>
                    <div className="relative">
                      <Input
                        id="inst-login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        required
                        className="rounded-sm mt-1 pr-10"
                        data-testid="inst-login-password-input"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm" data-testid="inst-login-submit-btn">
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="inst-reg-name" className="text-sm font-medium text-slate-700">Contact Person Name</Label>
                    <Input
                      id="inst-reg-name"
                      placeholder="Dr. John Smith"
                      value={registerForm.full_name}
                      onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })}
                      required
                      className="rounded-sm mt-1"
                      data-testid="inst-register-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="inst-reg-institution" className="text-sm font-medium text-slate-700">Institution Name</Label>
                    <Input
                      id="inst-reg-institution"
                      placeholder="ABC Medical Institute"
                      value={registerForm.institution_name}
                      onChange={(e) => setRegisterForm({ ...registerForm, institution_name: e.target.value })}
                      required
                      className="rounded-sm mt-1"
                      data-testid="inst-register-institution-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="inst-reg-email" className="text-sm font-medium text-slate-700">Email Address</Label>
                    <Input
                      id="inst-reg-email"
                      type="email"
                      placeholder="institution@example.com"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      required
                      className="rounded-sm mt-1"
                      data-testid="inst-register-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="inst-reg-password" className="text-sm font-medium text-slate-700">Password</Label>
                    <Input
                      id="inst-reg-password"
                      type="password"
                      placeholder="Create a strong password"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      required
                      className="rounded-sm mt-1"
                      data-testid="inst-register-password-input"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm" data-testid="inst-register-submit-btn">
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-xs text-slate-500 text-center mt-4">
          For institutions seeking accreditation for examination centers or training programs.
        </p>
      </div>
    </div>
  );
}
